/**
 * React hook for dashboard telemetry.
 * Provides typed helpers for common dashboard interactions.
 */
import { useCallback, useRef } from "react";
import { trackEvent, trackMetric } from "./index";

export type DashboardAction =
    // Navigation & page lifecycle
    | "page_loaded"
    | "project_switched"
    | "project_picker_opened"
    // Refresh
    | "refresh_clicked"
    // Time range
    | "time_range_changed"
    // Run history chart
    | "run_selected"
    | "run_selected_older"      // selected a run that is NOT the most recent
    | "run_deselected"
    // Stats row
    | "stats_visible"
    // Test results table
    | "test_expanded"
    | "test_collapsed"
    | "test_search_used"
    | "test_filter_applied"
    // Video
    | "video_played"
    | "video_closed"
    // Activity sidebar & popup
    | "activity_popup_opened"
    | "activity_popup_closed"
    | "activity_search_used"
    | "activity_filter_changed"
    // Reports
    | "heal_report_viewed"
    | "scout_report_viewed"
    // User menu
    | "user_menu_opened"
    | "directory_switched"
    | "signed_out";

export function useTelemetry() {
    const track = useCallback(
        (action: DashboardAction, properties?: Record<string, string>) => {
            trackEvent(`dashboard.${action}`, properties);
        },
        [],
    );

    const trackTiming = useCallback(
        (name: string, durationMs: number, properties?: Record<string, string>) => {
            trackMetric(`dashboard.timing.${name}`, durationMs, properties);
        },
        [],
    );

    return { track, trackTiming };
}

/**
 * Hook that measures time from mount to when `markLoaded()` is called.
 * Emits a timing metric once.
 */
export function useLoadTimer(name: string) {
    const startRef = useRef(Date.now());
    const firedRef = useRef(false);

    const markLoaded = useCallback(
        (properties?: Record<string, string>) => {
            if (firedRef.current) return;
            firedRef.current = true;
            const elapsed = Date.now() - startRef.current;
            trackMetric(name, elapsed, properties);
            trackEvent(`dashboard.page_loaded`, {
                ...properties,
                loadTimeMs: String(elapsed),
            });
        },
        [name],
    );

    return { markLoaded };
}
